import { useCallback } from 'react';
import { Drawer, Button } from '@mui/material';
import { useDeletePostMutation, useDeleteCommentMutation } from '../../../queries';
import toast from 'react-hot-toast';
import { useTranslation } from '../../../hook/useTranslation';

interface OptionDialogProps {
  open: boolean;
  onClose?: (event?: React.SyntheticEvent | {}, reason?: string) => void;
  selectedId: number;
  isUsersPost?: boolean;
  isPostNotComment?: boolean;
  reload: () => void;
  onComplaintClick?: (id: number) => void;
}

export default function OptionDialog({
  open,
  onClose = () => {},
  selectedId,
  isUsersPost,
  isPostNotComment,
  reload,
  onComplaintClick = () => {},
}: OptionDialogProps) {
  const { t } = useTranslation();
  const { mutate: deletePost } = useDeletePostMutation();
  const { mutate: deleteComment } = useDeleteCommentMutation();

  const handleDeletePost = useCallback(() => {
    if (!selectedId) return;
    deletePost(selectedId, {
      onSuccess: () => {
        onClose();
        reload();
      },
      onError: (error) => {
        console.error('Failed to delete post', error);
        toast.error(t('common.toast_delete_post_fail'));
      },
    });
  }, [selectedId, onClose, reload, deletePost, t]);

  const handleDeleteComment = useCallback(() => {
    if (!selectedId) return;
    deleteComment(selectedId, {
      onSuccess: () => {
        onClose();
        reload();
      },
      onError: (error) => {
        console.error('Failed to delete comment', error);
        toast.error(t('common.toast_delete_comment_fail'));
      },
    });
  }, [selectedId, onClose, reload, deleteComment, t]);

  return (
    <Drawer
      open={open}
      anchor="bottom"
      onClose={(event: any, reason: any) => {
        if (reason === 'backdropClick') return;
        if (onClose) onClose(event, reason);
      }}
      PaperProps={{ sx: { borderRadius: '10px 10px 0 0' } }}
    >
      <div className="relative w-full p-6 min-h-[100px] text-[#1C1C1C] text-[20px] font-[700] font-['Pretendard'] flex flex-col gap-[14px] items-center justify-between bg-white overflow-hidden">
        {isUsersPost && (
          <Button
            variant="outlined"
            fullWidth
            sx={{
              color: '#fff',
              bgcolor: '#FF3E3E',
              height: '56px',
              '&:hover': { color: '#fff', bgcolor: '#FF3E3E' },
            }}
            onClick={() => {
              if (isPostNotComment) handleDeletePost();
              else handleDeleteComment();
            }}
          >
            <span className="text-[18px]">{t('common.delete')}</span>
          </Button>
        )}
        <Button
          variant="outlined"
          fullWidth
          sx={{
            color: isUsersPost ? '#363636' : '#fff',
            bgcolor: isUsersPost ? '#FFF' : '#FF3E3E',
            height: '56px',
            '&:hover': {
              color: isUsersPost ? '#363636' : '#fff',
              bgcolor: isUsersPost ? '#F5F5F5' : '#FF3E3E',
            },
          }}
          onClick={() => onComplaintClick(selectedId)}
        >
          <span className="text-[18px]">{t('common.report_abuse')}</span>
        </Button>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ color: '#969696', mt: '12px', fontSize: '18px', '&:hover': { color: '#969696' } }}
          fullWidth
        >
          {t('common.close')}
        </Button>
      </div>
    </Drawer>
  );
}
